import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
	approveTransferRequest, 
	fetchLocationsForTransfer, 
	fetchTransferRequests, 
	fetchUsersForTransfer, 

	fetchVaultTransactions,
	fetchVaultToUserTransferRequests, 
	fetchUserToVaultTransferRequests, 
	initiateTransferRequest, 
	rejectTransferRequest, 
	TransferRequest 
} from '../../actions/transferActions';

interface User {
	id: string;
	fullname: string;
	email: string;
	firstname: string;
	lastname: string;
}

interface Location {
	id: string;
	locationName: string;
	locationCode: string;
	locationBalance?: {
		balance: number;
	};
}

interface VaultTransaction {
	id: string;
	cashierShiftId: string;
	amount: string;
	type: 'cashier_to_vault' | 'vault_to_cashier' | 'vault_bank_deposit' | 'expense';
	status: string;
	totalAmount: string;
	fromCurrency: string;
	toCurrency: string;
	oldBalance: string;
	newBalance: string;
	convertedAmount: string;
	serviceFee: string;
	exchangeRate: string;
	userId: string;
	paymentMethod?: string;
	createdAt: string;
	updatedAt: string;
}

interface TransferState {
	// Transfer requests
	transferRequests: TransferRequest[];
	pagination: {
		total: number;
		totalPages: number;
		currentPage: number;
		limit: number;
		hasNextPage: boolean;
		hasPrevPage: boolean;
	} | null;
	stats: {
		total: number;
		pending: number;
		approved: number;
		rejected: number;
	} | null;
	
	// Vault transactions
	vaultTransactions: VaultTransaction[];
	vaultPagination: {
		total: number;
		totalPages: number;
		currentPage: number;
		limit: number;
		hasNextPage: boolean;
		hasPrevPage: boolean;
	} | null;
	
	// Loading states
	isLoading: boolean;
	isLoadingVault: boolean;
	isInitiating: boolean;
	isApproving: boolean;
	isRejecting: boolean;
	isFetchingUsers: boolean;
	isFetchingLocations: boolean;
	
	// Data for dropdowns
	users: User[];
	locations: Location[];
	
	// Error states
	error: string | null;
	vaultError: string | null;
	initiateError: string | null;
	approveError: string | null;
	rejectError: string | null;
	usersError: string | null;
	locationsError: string | null;
	
	// Currently processing request ID
	processingRequestId: string | null;
}

const initialState: TransferState = {
	transferRequests: [],
	pagination: null,
	stats: null,
	vaultTransactions: [],
	vaultPagination: null,
	isLoading: false,
	isLoadingVault: false,
	isInitiating: false,
	isApproving: false,
	isRejecting: false,
	isFetchingUsers: false,
	isFetchingLocations: false,
	users: [],
	locations: [],
	error: null,
	vaultError: null,
	initiateError: null,
	approveError: null,
	rejectError: null,
	usersError: null,
	locationsError: null,
	processingRequestId: null,
};

const transferSlice = createSlice({
	name: 'transfers',
	initialState,
	reducers: {
		clearErrors: (state) => {
			state.error = null;
			state.vaultError = null;
			state.initiateError = null;
			state.approveError = null;
			state.rejectError = null;
			state.usersError = null;
			state.locationsError = null;
		},
		setProcessingRequestId: (state, action: PayloadAction<string | null>) => {
			state.processingRequestId = action.payload;
		},
		clearTransferState: (state) => {
			state.transferRequests = [];
			state.pagination = null;
			state.stats = null;
			state.users = [];
			state.locations = [];
			state.error = null;
			state.initiateError = null;
			state.approveError = null;
			state.rejectError = null;
			state.usersError = null;
			state.locationsError = null;
			state.processingRequestId = null;
		},
	},
	extraReducers: (builder) => {
		// Fetch transfer requests
		builder
			.addCase(fetchTransferRequests.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchTransferRequests.fulfilled, (state, action) => {
				state.isLoading = false;
				state.transferRequests = action.payload.data;
				state.pagination = action.payload.pagination;
				state.stats = action.payload.stats || null;
				state.error = null;
			})
			.addCase(fetchTransferRequests.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload as string;
				state.transferRequests = [];
				state.pagination = null;
				state.stats = null;
			});

		builder
			.addCase(fetchVaultToUserTransferRequests.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchVaultToUserTransferRequests.fulfilled, (state, action) => {
				state.isLoading = false;
				state.transferRequests = action.payload.data;
				state.pagination = action.payload.pagination;
				state.stats = action.payload.stats || null;
				state.error = null;
			})
			.addCase(fetchVaultToUserTransferRequests.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload as string;
				state.transferRequests = [];
				state.pagination = null;
				state.stats = null;
			});

		builder
			.addCase(fetchUserToVaultTransferRequests.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchUserToVaultTransferRequests.fulfilled, (state, action) => {
				state.isLoading = false;
				state.transferRequests = action.payload.data;
				state.pagination = action.payload.pagination;
				state.stats = action.payload.stats || null;
				state.error = null;
			})
			.addCase(fetchUserToVaultTransferRequests.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload as string;
				state.transferRequests = [];
				state.pagination = null;
				state.stats = null;
			});

		// Initiate transfer request
		builder
			.addCase(initiateTransferRequest.pending, (state) => {
				state.isInitiating = true;
				state.initiateError = null;
			})
			.addCase(initiateTransferRequest.fulfilled, (state) => {
				state.isInitiating = false;
				state.initiateError = null;
			})
			.addCase(initiateTransferRequest.rejected, (state, action) => {
				state.isInitiating = false;
				state.initiateError = action.payload as string;
			});

		builder
			.addCase(approveTransferRequest.pending, (state, action) => {
				state.isApproving = true;
				state.approveError = null;
				state.processingRequestId = action.meta.arg.requestId;
			})
			.addCase(approveTransferRequest.fulfilled, (state, action) => {
				state.isApproving = false;
				state.approveError = null;
				state.processingRequestId = null;
				const requestIndex = state.transferRequests.findIndex(
					req => req.id === action.payload.requestId
				);
				if (requestIndex !== -1) {
					const oldStatus = state.transferRequests[requestIndex].status;
					state.transferRequests[requestIndex].status = 'approved';
					state.transferRequests[requestIndex].updatedAt = new Date().toISOString();
					
					if (state.stats) {
						if (oldStatus === 'pending') state.stats.pending -= 1;
						state.stats.approved += 1;
					}
				}
			})
			.addCase(approveTransferRequest.rejected, (state, action) => {
				state.isApproving = false;
				state.approveError = action.payload as string;
				state.processingRequestId = null;
			});

		builder
			.addCase(rejectTransferRequest.pending, (state, action) => {
				state.isRejecting = true;
				state.rejectError = null;
				state.processingRequestId = action.meta.arg.requestId;
			})
			.addCase(rejectTransferRequest.fulfilled, (state, action) => {
				state.isRejecting = false;
				state.rejectError = null;
				state.processingRequestId = null;
				const requestIndex = state.transferRequests.findIndex(
					req => req.id === action.payload.requestId
				);
				if (requestIndex !== -1) {
					const oldStatus = state.transferRequests[requestIndex].status;
					state.transferRequests[requestIndex].status = 'rejected';
					state.transferRequests[requestIndex].updatedAt = new Date().toISOString();
					
					if (state.stats) {
						if (oldStatus === 'pending') state.stats.pending -= 1;
						state.stats.rejected += 1;
					}
				}
			})
			.addCase(rejectTransferRequest.rejected, (state, action) => {
				state.isRejecting = false;
				state.rejectError = action.payload as string;
				state.processingRequestId = null;
			});

		// Fetch users for transfer
		builder
			.addCase(fetchUsersForTransfer.pending, (state) => {
				state.isFetchingUsers = true;
				state.usersError = null;
			})
			.addCase(fetchUsersForTransfer.fulfilled, (state, action) => {
				state.isFetchingUsers = false;
				state.users = action.payload.data || [];
				state.usersError = null;
			})
			.addCase(fetchUsersForTransfer.rejected, (state, action) => {
				state.isFetchingUsers = false;
				state.usersError = action.payload as string;
				state.users = [];
			});

		// Fetch locations for transfer
		builder
			.addCase(fetchLocationsForTransfer.pending, (state) => {
				state.isFetchingLocations = true;
				state.locationsError = null;
			})
			.addCase(fetchLocationsForTransfer.fulfilled, (state, action) => {
				state.isFetchingLocations = false;
				state.locations = action.payload.data || [];
				state.locationsError = null;
			})
			.addCase(fetchLocationsForTransfer.rejected, (state, action) => {
				state.isFetchingLocations = false;
				state.locationsError = action.payload as string;
				state.locations = [];
			});

		// Fetch vault transactions
		builder
			.addCase(fetchVaultTransactions.pending, (state) => {
				state.isLoadingVault = true;
				state.vaultError = null;
			})
			.addCase(fetchVaultTransactions.fulfilled, (state, action) => {
				state.isLoadingVault = false;
				state.vaultTransactions = action.payload.data;
				state.vaultPagination = action.payload.pagination;
				state.vaultError = null;
			})
			.addCase(fetchVaultTransactions.rejected, (state, action) => {
				state.isLoadingVault = false;
				state.vaultError = action.payload as string;
				state.vaultTransactions = [];
				state.vaultPagination = null;
			});
	},
});

export const { clearErrors, setProcessingRequestId, clearTransferState } = transferSlice.actions;
export default transferSlice.reducer;